// "Upload document" dialog of the Assessments page: the capability uploader, then the reading of the new document followed live with "Open document" and "New assessment" once it is ready; the same PDF again closes the dialog and opens the stored document.
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, PlusIcon } from "lucide-react";
import { useState } from "react";

import type { CapabilityDocumentDetail } from "@/api/__generated__";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { notify } from "@/components/ui/sonner";

import { CapabilityUploader } from "./CapabilityUploader";
import { documentTitle } from "./labels";

interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  region: string;
  onUploaded?: () => void;
}

export function UploadDocumentDialog({ open, onOpenChange, region, onUploaded }: UploadDocumentDialogProps) {
  const navigate = useNavigate();
  const [round, setRound] = useState(0);
  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setRound((value) => value + 1);
    }
  };
  const openDocument = (document: CapabilityDocumentDetail) => {
    close(false);
    void navigate({
      to: "/$region/assessments/documents/$capabilityDocUid",
      params: { region, capabilityDocUid: document.capability_doc_uid },
    });
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Add a document to compare</DialogTitle>
          <DialogDescription>
            A PDF of the product: what the turbines and the plant controller can do. Its entries are read
            automatically, then you can check it against the grid code.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <CapabilityUploader
            key={round}
            onStored={onUploaded}
            onDuplicate={(document) => {
              notify("Already uploaded", `${documentTitle(document)} opens instead.`);
              openDocument(document);
            }}
            footer={(document) => (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    openDocument(document);
                  }}
                >
                  <ArrowRightIcon aria-hidden />
                  Open document
                </Button>
                {document.status === "ready" ? (
                  <Button asChild size="sm">
                    <Link
                      to="/$region/assessments/new"
                      params={{ region }}
                      search={{ doc: document.capability_doc_uid }}
                    >
                      <PlusIcon aria-hidden />
                      New assessment
                    </Link>
                  </Button>
                ) : null}
              </div>
            )}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              close(false);
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
